import type { IHttpClient } from '@wix/http-client';
import type {
  CompleteSetupCountRequest,
  ManagedAppsRequest,
} from '@wix/ambassador-devcenter-managedapps-v1-managed-app/types';
import type { GetAppsRuntimeDataRequest } from '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/types';
import type { QueryTagRequest } from '@wix/ambassador-prime-auto-v1-tag/types';
import type {
  GetAppsByTagRequest,
  QueryTagAppRequest,
} from '@wix/ambassador-devcenter-appmarket-v1-tag-app/types';
import type {
  GetAppsByAppIdsRequest,
  GetDynamicSectionsRequest,
  GetTagSectionsRequest,
} from '@wix/ambassador-appmarket-v1-app-market-client-aggregator/types';
import type { GetWebSolutionsRequest } from '@wix/ambassador-devcenter-marketplace-v1-web-solution/types';
import type { ListAppPlansByAppIdRequest } from '@wix/ambassador-appmarket-v1-app-plans/types';
import type { CompleteInstallationRequest } from '@wix/ambassador-devcenter-pendingapps-v1-pending-installation/types';
import type { PropertiesReadResponse } from '@wix/ambassador-site-properties-service/types';
import type { GetSitesPremiumStatusRequest } from '@wix/ambassador-premium-v1-asset/types';
import type {
  AutocompleteRequest,
  SearchRequest,
} from '@wix/ambassador-devcenter-ams-v1-app-market-search/types';
import type { GetFreeTrialEligibilityRequest } from '@wix/ambassador-premium-store-v1-dynamic-offering-service-entity/types';
import type { GetEligibleAppsForFreeTrialRequest } from '@wix/ambassador-appmarket-v1-app-subscriptions/types';
import type { Permission } from './app-permissions';
import type {
  ErrorCodesMapConditionalEnforcement,
  ErrorHandlerPublicAPI,
} from '@wix/error-handler';
import type { RequestOptions } from './request-service';

/**
 * Http services implementation, based on httpClient.
 * @class HttpServices
 */
export class HttpServices {
  constructor(
    private readonly httpClient: IHttpClient,
    private readonly errorHandler?: ErrorHandlerPublicAPI,
  ) {}

  private withErrorHandler<T>(
    fn: () => Promise<T>,
    options: { errorCodesMap: ErrorCodesMapConditionalEnforcement<T> },
  ) {
    if (this.errorHandler) {
      return this.errorHandler.withErrorHandler(fn, options).catch((err) => {
        this.errorHandler?.getResolvedError(err);
        throw err;
      });
    }
    return fn();
  }

  async getManagedApps(
    payload: ManagedAppsRequest,
    options: RequestOptions = {
      errorCodesMap: {},
    },
  ) {
    const managedApps = (
      await import('@wix/ambassador-devcenter-managedapps-v1-managed-app/http')
    ).managedApps;
    return this.withErrorHandler(
      () => this.httpClient.request(managedApps(payload)),
      options,
    );
  }

  async completeSetupCount(
    payload: CompleteSetupCountRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const completeSetupCount = (
      await import('@wix/ambassador-devcenter-managedapps-v1-managed-app/http')
    ).completeSetupCount;
    return this.withErrorHandler(
      () => this.httpClient.request(completeSetupCount(payload)),
      options,
    );
  }

  async queryTag(
    payload: QueryTagRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const queryTag = (await import('@wix/ambassador-prime-auto-v1-tag/http'))
      .queryTag;
    return this.withErrorHandler(
      () => this.httpClient.request(queryTag(payload)),
      options,
    );
  }

  async queryTagApp(
    payload: QueryTagAppRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const queryTagApp = (
      await import('@wix/ambassador-devcenter-appmarket-v1-tag-app/http')
    ).queryTagApp;
    return this.withErrorHandler(
      () => this.httpClient.request(queryTagApp(payload)),
      options,
    );
  }
  async getAppsByTag(
    payload: GetAppsByTagRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const getAppsByTag = (
      await import('@wix/ambassador-devcenter-appmarket-v1-tag-app/http')
    ).getAppsByTag;
    return this.withErrorHandler(
      () => this.httpClient.request(getAppsByTag(payload)),
      options,
    );
  }

  async getAppsByAppIds(
    payload: GetAppsByAppIdsRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const getAppsByAppIds = (
      await import(
        '@wix/ambassador-appmarket-v1-app-market-client-aggregator/http'
      )
    ).getAppsByAppIds;
    return this.withErrorHandler(
      () => this.httpClient.request(getAppsByAppIds(payload)),
      options,
    );
  }

  async getWebSolutionsBase(
    payload: GetWebSolutionsRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const getWebSolutions = (
      await import('@wix/ambassador-devcenter-marketplace-v1-web-solution/http')
    ).getWebSolutionsBase;
    return this.withErrorHandler(
      () => this.httpClient.request(getWebSolutions(payload)),
      options,
    );
  }

  async getDynamicSections(
    payload: GetDynamicSectionsRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const getDynamicSections = (
      await import(
        '@wix/ambassador-appmarket-v1-app-market-client-aggregator/http'
      )
    ).getDynamicSections;
    return this.withErrorHandler(
      () => this.httpClient.request(getDynamicSections(payload)),
      options,
    );
  }

  async queryAppPlans(
    payload: ListAppPlansByAppIdRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const queryAppPlans = (
      await import('@wix/ambassador-appmarket-v1-app-plans/http')
    ).listAppPlansByAppId;
    return this.withErrorHandler(
      () => this.httpClient.request(queryAppPlans(payload)),
      options,
    );
  }

  async completePendingInstallation(
    payload: CompleteInstallationRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const completeInstallation = (
      await import(
        '@wix/ambassador-devcenter-pendingapps-v1-pending-installation/http'
      )
    ).completeInstallation;
    return this.withErrorHandler(
      () => this.httpClient.request(completeInstallation(payload)),
      options,
    );
  }

  async getTagSections(
    payload: GetTagSectionsRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const getTagSections = (
      await import(
        '@wix/ambassador-appmarket-v1-app-market-client-aggregator/http'
      )
    ).getTagSections;
    return this.withErrorHandler(
      () => this.httpClient.request(getTagSections(payload)),
      options,
    );
  }

  async getSiteProperties(options: RequestOptions = { errorCodesMap: {} }) {
    const res = await this.withErrorHandler(
      () =>
        this.httpClient.get<PropertiesReadResponse>(
          '/_api/site-properties-service/properties',
        ),
      options,
    );
    return res.data;
  }

  async getSitesPremiumStatus(
    payload: GetSitesPremiumStatusRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const getSitesPremiumStatus = (
      await import('@wix/ambassador-premium-v1-asset/http')
    ).getSitesPremiumStatus;
    return this.withErrorHandler(
      () => this.httpClient.request(getSitesPremiumStatus(payload)),
      options,
    );
  }

  async searchApps(
    payload: SearchRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const searchApps = (
      await import('@wix/ambassador-devcenter-ams-v1-app-market-search/http')
    ).search;
    return this.withErrorHandler(
      () => this.httpClient.request(searchApps(payload)),
      options,
    );
  }

  async getAppsRuntimeData(
    payload: GetAppsRuntimeDataRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const getAppsRuntimeData = (
      await import(
        '@wix/ambassador-devcenter-appsruntimereader-v1-app-runtime-data/http'
      )
    ).getAppsRuntimeData;
    return this.withErrorHandler(
      () => this.httpClient.request(getAppsRuntimeData(payload)),
      options,
    );
  }

  async getAutoCompleteSuggestions(
    payload: AutocompleteRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const autocompleteSuggestions = (
      await import('@wix/ambassador-devcenter-ams-v1-app-market-search/http')
    ).autocomplete;
    return this.withErrorHandler(
      () => this.httpClient.request(autocompleteSuggestions(payload)),
      options,
    );
  }

  async getAutoCompleteProxySuggestions(payload: {
    searchTerm: string;
    languageCode: string;
  }): Promise<string[]> {
    const { searchTerm, languageCode } = payload;
    const res = await this.withErrorHandler(
      () =>
        this.httpClient.get<{ autocomplete: string[] }>(
          `/_serverless/app-market-search/autocomplete?term=${searchTerm}&lang=${languageCode}`,
        ),
      { errorCodesMap: {} },
    );
    return res.data.autocomplete ?? [];
  }

  async getFreeTrialEligibility(
    payload: GetFreeTrialEligibilityRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const getFreeTrialEligibility = (
      await import(
        '@wix/ambassador-premium-store-v1-dynamic-offering-service-entity/http'
      )
    ).getFreeTrialEligibility;
    return this.withErrorHandler(
      () => this.httpClient.request(getFreeTrialEligibility(payload)),
      options,
    );
  }

  async getEligibleAppsForFreeTrial(
    payload: GetEligibleAppsForFreeTrialRequest,
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const getEligibleAppsForFreeTrial = (
      await import('@wix/ambassador-appmarket-v1-app-subscriptions/http')
    ).getEligibleAppsForFreeTrial;
    return this.withErrorHandler(
      () => this.httpClient.request(getEligibleAppsForFreeTrial(payload)),
      options,
    );
  }

  async getAppPermissions(
    payload: { appId: string; languageCode: string },
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const { appId, languageCode } = payload;
    const res = await this.withErrorHandler(
      () =>
        this.httpClient.get<Permission[]>(
          `/_serverless/app-installer-fed-serverless/app-permissions?appId=${appId}&languageCode=${languageCode}`,
        ),
      options,
    );
    return res.data;
  }

  async getSignedAppMarketParams(
    {
      appMarketParamsPayload,
    }: {
      appMarketParamsPayload: string;
    },
    options: RequestOptions = { errorCodesMap: {} },
  ) {
    const res = await this.withErrorHandler(
      () =>
        this.httpClient.post<string>(
          '/_serverless/app-market-json-web-token/json-web-token',
          {
            data: appMarketParamsPayload,
          },
        ),
      options,
    );
    return res.data;
  }

  async postSupportEmail(
    payload: {
      description: string;
      topic: string;
      appInstanceId: string;
      appId: string;
      msid: string;
    },
    options: RequestOptions = { errorCodesMap: {} },
  ): Promise<void> {
    await this.withErrorHandler(
      () => this.httpClient.post('/_serverless/app-support/send-mail', payload),
      options,
    );
  }
}
